'use client';

import { cn } from '@insta-club/utilities';
import { forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { FloatingTextareaProps } from './types';
import { validateTextarea } from './utils';
import { containerVariants, floatingLabelVariants } from './variants';

const Textarea = forwardRef<HTMLTextAreaElement, FloatingTextareaProps>(
    (
        {
            containerClassName,
            variant,
            size,
            label,
            disabled,
            readOnly,
            textareaType = 'text',
            onChange,
            name,
            fullWidth = true,
            max,
            maxLength,
            ...props
        },
        ref
    ) => {
        const {
            register,
            watch,
            formState: { errors }
        } = useFormContext();

        const value = watch(name) ?? '';
        const currentLength = typeof value === 'string' ? value.length : String(value ?? '').length;
        const effectiveMaxLength =
            typeof maxLength === 'number' ? maxLength : typeof max === 'number' ? max : undefined;

        const { onChange: formOnChange, ref: formRef, ...restRegister } = register(name);

        const inputVariant = errors[name] ? 'error' : variant;

        const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const rawValue = e.target.value;
            const validatedValue = validateTextarea(rawValue, textareaType);

            if (validatedValue !== rawValue) {
                e.target.value = validatedValue;
            }

            if (onChange) {
                onChange(e);
            }
        };

        return (
            <div
                className={cn('flex flex-col gap-1', {
                    'w-full': fullWidth
                })}
            >
                <div
                    className={cn(
                        containerVariants({ variant: inputVariant, size }),
                        disabled && 'cursor-not-allowed bg-gray-50 opacity-50',
                        containerClassName
                    )}
                >
                    <textarea
                        className={cn(
                            'peer w-full resize-none bg-transparent text-base font-medium placeholder-transparent focus:ring-0 focus:outline-none'
                        )}
                        placeholder=' '
                        disabled={disabled}
                        readOnly={readOnly}
                        dir='rtl'
                        ref={(el) => {
                            if (typeof ref === 'function') {
                                ref(el);
                            } else if (ref) {
                                (ref as React.RefObject<HTMLTextAreaElement | null>).current = el;
                            }
                            formRef(el);
                        }}
                        onChange={(e) => {
                            handleChange(e);
                            formOnChange(e);
                        }}
                        maxLength={effectiveMaxLength}
                        {...props}
                        {...restRegister}
                    />
                    {label && (
                        <label
                            className={cn(
                                floatingLabelVariants({ variant: inputVariant, size }),
                                disabled && 'bg-gray-50'
                            )}
                        >
                            {label}
                        </label>
                    )}
                </div>
                <div className='flex items-center justify-between'>
                    {errors && errors[name] && (
                        <p className='flex-1 text-right text-sm text-red-500'>
                            {errors[name]?.message as unknown as string}
                        </p>
                    )}
                    {typeof effectiveMaxLength === 'number' && (
                        <p className='flex-1 text-left text-xs font-normal text-gray-400'>{`${currentLength}/${effectiveMaxLength}`}</p>
                    )}
                </div>
            </div>
        );
    }
);

Textarea.displayName = 'Textarea';

export default Textarea;

